import * as React from 'react'
import Breadcrumbs from "../components/Breadcrumbs/Breadcrumbs"
import ERoute from "../ERoute"
import styled from 'styled-components'
import ItemContainer from '../components/Item/ItemContainer'
import Card from '../components/Card/Card'
import Metadata from '../components/Metadata/Metadata'
import Avatar from '../components/Avatar/Avatar'
import StackedAvatars from '../components/Avatar/StackedAvatars'
import CardTitle from '../components/Card/CardTitle'
import { Link, useParams } from 'react-router-dom'
import HelpCenterController from '../controllers/HelpCenterController'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { AppState } from '../store'
import { Author, HelpCenterArticleCollection } from '../../types'
import PageLoader from '../components/Loader/PageLoader'
import MobilityHelper from '../../helpers/MobilityHelper'
import { articleLink, collectionLink, overviewLink } from '../helpers/NavigationHelper'
import { Helmet } from 'react-helmet'
import ItemWrapper from '../components/Item/ItemWrapper'
import ItemTitle from '../components/Item/ItemTitle'
import ItemDescription from '../components/Item/ItemDescription'

const Overview = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;

  h1 {
    color: #1a1a1a;
    line-height: 40px;
    font-weight: 600;
    font-size: 32px;
    margin-bottom: 4px;
  }

  p {
    font-size: 16px;
    margin: 0;
    font-weight: 300;
  }
`

const CollectionIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 56px;
  height: 56px;
  background: rgb(244, 243, 237);
  border-radius: 8px;

  img {
    width: 40px;
    height: 40px;
    max-width: 100%;
    height: auto;
  }
`

const Content = styled.div`
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const CollectionMetadata = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;

  span {
    padding-top: 8px;
    color: #737373;
    line-height: 24px;
    font-size: 14px;

  }
`

interface IProps {

}

interface IState {
  articleCollection: HelpCenterArticleCollection
  authors: Author[]
  articlesCount: number
  didInitialLoad: boolean
}

const Collection = (props: IProps) => {
  const { currentLocale, helpCenter } = useSelector((state: AppState) => state.helpCenter)
  const { id } = useParams<{ locale: string, id: string }>()

  const { t } = useTranslation()
  const [state, setState] = React.useState<IState>({
    articleCollection: null,
    authors: [],
    articlesCount: 0,
    didInitialLoad: false,
  })

  const { didInitialLoad, articleCollection, authors, articlesCount } = state

  const fetchCollection = async () => {
    try {
      const { article_collection, authors, articles_count } = await HelpCenterController.getCollection(helpCenter.id, id)

      setState({
        articleCollection: article_collection,
        authors: authors,
        articlesCount: articles_count,
        didInitialLoad: true,
      })
    } catch (ex) {
      console.error(ex)
    }
  }

  React.useEffect(() => {
    fetchCollection().catch(console.error)
  }, [id])

  const getBreadcrumbs = () => {
    let breadCrumbs: { label: string, to: string }[] = [
      {
        label: t('Collection::All collections'),
        to: overviewLink(currentLocale)
      },
    ]

    let crumbs: { label: string, to: string }[] = []

    if (articleCollection.article_collection) {
      const parentCollection = articleCollection.article_collection

      crumbs = [
        ...crumbs,
        {
          label: MobilityHelper.getTranslation(currentLocale, parentCollection.translations, 'title'),
          to: collectionLink(parentCollection)
        }
      ]

      if (parentCollection.article_collection) {
        const grandParentCollection = parentCollection.article_collection

        crumbs = [
          ...crumbs,
          {
            label: MobilityHelper.getTranslation(currentLocale, grandParentCollection.translations, 'title'),
            to: collectionLink(grandParentCollection)
          }
        ]
      }

      crumbs.reverse()
    }

    breadCrumbs = [
      ...breadCrumbs,
      ...crumbs,
      {
        label: MobilityHelper.getTranslation(currentLocale, articleCollection.translations, 'title'),
        to: collectionLink(articleCollection)
      }]

    return breadCrumbs
  }

  if (!didInitialLoad) {
    return <PageLoader />
  }

  const title = MobilityHelper.getTranslation(currentLocale, articleCollection.translations, 'title')
  const description = MobilityHelper.getTranslation(currentLocale, articleCollection.translations, 'description')
  const articles = articleCollection.articles || []
  const localeArticlesCount = articles.filter(article => Boolean(MobilityHelper.getTranslation(currentLocale, article.translations, 'title'))).length
  const collections = articleCollection.article_collections || []

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
      </Helmet>

      <Breadcrumbs breadcrumbs={getBreadcrumbs()} />

      <Overview>
        <CollectionIcon>
          <img src={articleCollection.icon} />
        </CollectionIcon>

        <h1>{title}</h1>
        <p>{description}</p>

        <Metadata style={{ marginTop: 20 }}>
          {helpCenter.show_article_authors && <>
            <StackedAvatars>
              {authors.slice(0, 3).map((author) => (
                <Avatar key={author.id} name={author.name} src={author.avatar_url} />
              ))}
            </StackedAvatars>
            {authors?.length > 0 && <>
              {authors.length === 1 && <span>{t('Collection::By {{name}}', { name: authors[0]?.name || helpCenter.business_name })}</span>}
              {authors.length > 1 && <span>{t('Collection::By {{name}} and {{count}} others', { name: authors[0]?.name || helpCenter.business_name, count: articleCollection.authors_count - 1 })}</span>}
              {authors.length > 0 && <span className='mobile-show'>{t('Collection::{{count}} authors', { count: articleCollection.authors_count })}</span>}
              <svg width="4" height="4" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill="currentColor"  ><path d="M15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15C11.866 15 15 11.866 15 8Z"></path></svg>
            </>}
          </>}
          {t('Collection::{{count}} articles', { count: articleCollection.articles_count })}
        </Metadata>

        <Content>
          {localeArticlesCount > 0 && <Card>
            {articles.map((article) => {
              return (
                <ItemContainer key={article.id} to={articleLink(article)}>
                  <ItemWrapper>
                    <ItemTitle>{MobilityHelper.getTranslation(currentLocale, article.translations, 'title')}</ItemTitle>
                    {helpCenter.collection_descriptions_enabled && <ItemDescription style={{ marginTop: 4 }}>{MobilityHelper.getTranslation(currentLocale, article.translations, 'summary')}</ItemDescription>}
                  </ItemWrapper>

                  <svg fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
                </ItemContainer>
              )
            })}
          </Card>}

          {collections?.map(collection => {
            if (collection?.article_collections?.length === 0 && collection.articles?.length === 0) return null

            const localeArticleCollectionsCount = collection.article_collections.filter(subCollection => Boolean(MobilityHelper.getTranslation(currentLocale, subCollection.translations, 'title'))).length
            const localeArticlesCount = collection.articles.filter(article => Boolean(MobilityHelper.getTranslation(currentLocale, article.translations, 'title'))).length

            return (
              <Card key={collection.id}>
                <CardTitle>
                  <Link to={collectionLink(collection)}>
                    <h2>{MobilityHelper.getTranslation(currentLocale, collection.translations, 'title')}</h2>
                    {helpCenter.collection_descriptions_enabled && <p style={{ marginTop: 8 }}>{MobilityHelper.getTranslation(currentLocale, collection.translations, 'description')}</p>}
                  </Link>
                </CardTitle>

                {localeArticleCollectionsCount > 0 && collection.article_collections?.map((subCollection) => {
                  return (
                    <ItemContainer key={subCollection.id} to={collectionLink(subCollection)}>
                      <CollectionMetadata>
                        <ItemTitle>
                          {MobilityHelper.getTranslation(currentLocale, subCollection.translations, 'title')}
                        </ItemTitle>

                        {helpCenter.collection_descriptions_enabled && <ItemDescription style={{ paddingTop: 8 }}>
                          {MobilityHelper.getTranslation(currentLocale, subCollection.translations, 'description')}
                        </ItemDescription>}

                        <span>{t('Collection::{{count}} articles', { count: subCollection.articles_count })}</span>
                      </CollectionMetadata>
                      <svg fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
                    </ItemContainer>
                  )
                })}

                {localeArticlesCount > 0 && collection.articles?.map((article) => {
                  return (
                    <ItemContainer key={article.id} to={articleLink(article)}>
                      {MobilityHelper.getTranslation(currentLocale, article.translations, 'title')}

                      <svg fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
                    </ItemContainer>
                  )
                })}
              </Card>
            )
          })}
        </Content>
      </Overview >

    </>
  )

}

export default Collection